import { useEffect, useRef, useState } from 'react'
import { authClient } from '@web/auth'
import { describeSignInError } from '@web/router'

export { OTP_LENGTH } from '@web/screens/auth/flow'

export const RESEND_SECONDS = 30

function errorCode(error: { code?: string; status?: number; message?: string }): string {
	return error.code ?? error.message ?? String(error.status ?? 'unknown')
}

export interface EmailCode {
	pending: boolean
	failure: string | null
	sending: boolean
	cooldown: number
	verify: (code: string) => Promise<void>
	resend: () => Promise<void>
}

export function useEmailCode({
	email,
	redirectTo,
	error = null,
	resendSeconds = RESEND_SECONDS,
}: {
	email: string
	redirectTo: string
	error?: string | null
	resendSeconds?: number
}): EmailCode {
	const [pending, setPending] = useState(false)
	const [sending, setSending] = useState(false)
	const [failure, setFailure] = useState<string | null>(null)
	const [cooldown, setCooldown] = useState(resendSeconds)

	const latest = useRef({ email, redirectTo })
	latest.current = { email, redirectTo }

	useEffect(() => {
		if (resendSeconds === 0) return
		const timer = setInterval(() => setCooldown((seconds) => (seconds === 0 ? 0 : seconds - 1)), 1000)
		return () => clearInterval(timer)
	}, [resendSeconds])

	async function verify(code: string) {
		setFailure(null)
		setPending(true)
		try {
			const result = await authClient.signIn.emailOtp({ email: latest.current.email, otp: code })
			if (result.error) {
				setFailure(describeSignInError(errorCode(result.error)))
				return
			}
			window.location.assign(latest.current.redirectTo)
		} finally {
			setPending(false)
		}
	}

	async function resend() {
		if (cooldown > 0 || sending) return
		setFailure(null)
		setSending(true)
		try {
			const result = await authClient.emailOtp.sendVerificationOtp({ email: latest.current.email, type: 'sign-in' })
			if (result.error) {
				setFailure(describeSignInError(errorCode(result.error)))
				return
			}
			setCooldown(resendSeconds)
		} finally {
			setSending(false)
		}
	}

	return {
		pending,
		failure: failure ?? (error ? describeSignInError(error) : null),
		sending,
		cooldown,
		verify,
		resend,
	}
}
